import React, {useState, useMemo, useEffect} from 'react';

import {useMutation, useQuery} from '@apollo/client/react';
import {X, Users, AlertCircle, CheckCircle, UserPlus} from 'lucide-react';
import {RadioCardSelect} from '../RadioCardSelect';
import {gql} from 'types/__generated__';
import type {FindUsersForOrgAssignmentQuery} from 'types/__generated__/graphql';
import {ORGANISATION_ROLES_QUERY, grantableRoles} from '../../graphql/roles';

const CREATE_USER = gql(`
  mutation CreateUser($input: UserInput!) {
    createUser(input: $input)
  }
`);

const FIND_USERS_QUERY = gql(`
  query FindUsersForOrgAssignment($username: String, $paging: Paging) {
    findUsersInAnyOrganisation(username: $username, paging: $paging) {
      users {
        id
        username
        email
        firstName
        lastName
        name
        enabled
        acceptedTerms
        isSSO
        organisationMemberships {
          name
          organisationId
        }
      }
      page
      totalHits
    }
  }
`);

const SET_PERMISSION_GROUP = gql(`
  mutation SetPermissionGroupForUsers($usernames: [String!]!, $organisationId: UUID, $permissionGroupId: UUID!) {
    setPermissionGroupForUsers(usernames: $usernames, organisationId: $organisationId, permissionGroupId: $permissionGroupId)
  }
`);

interface AssignUsersToOrgModalProps {
  organisationId: string;
  organisationName: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/** A user as FindUsersForOrgAssignment selects it. */
type AssignableUser =
  FindUsersForOrgAssignmentQuery['findUsersInAnyOrganisation']['users'][number];

interface UserAssignment {
  user: AssignableUser;
  permissionGroupId: string;
}

type WizardStep = 'select' | 'permissions' | 'confirm' | 'result';

export default function AssignUsersToOrgModal({organisationId, organisationName, isOpen, onClose, onSuccess}: AssignUsersToOrgModalProps) {
  const [step, setStep] = useState<WizardStep>('select');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [userAssignments, setUserAssignments] = useState<Map<string, string>>(new Map());
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [basketUsers, setBasketUsers] = useState<Set<string>>(new Set()); // Shopping basket
  const [basketUserDetails, setBasketUserDetails] = useState<Map<string, AssignableUser>>(new Map()); // Store full user objects
  const [importResults, setImportResults] = useState<{
    success: number;
    failed: number;
    errors: Array<{username: string; error: string}>;
  } | null>(null);

  const [createUser, {loading: assigningUsers}] = useMutation(CREATE_USER);
  const [setPermissionGroup] = useMutation(SET_PERMISSION_GROUP);

  // The target organisation's own roles (built-in and custom), not the admin login's.
  const {data: rolesData} = useQuery(ORGANISATION_ROLES_QUERY, {
    variables: {id: organisationId},
    skip: !isOpen || !organisationId,
    fetchPolicy: 'cache-first',
  });

  // Debounce search term - search automatically after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('[AssignUsersModal] Debounce timer fired, setting debouncedSearchTerm to:', searchTerm);
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay like in user detail list

    return () => {
      console.log('[AssignUsersModal] Clearing debounce timer');
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Query users from server based on debounced search term
  const {data: searchData, loading: searchLoading} = useQuery(FIND_USERS_QUERY, {
    variables: {
      username: debouncedSearchTerm.trim() || undefined,
      paging: {page: 0, pageSize: 100},
    },
    skip: !isOpen || !debouncedSearchTerm.trim(),
    fetchPolicy: 'network-only',
  });

  const roles = useMemo(() => grantableRoles(rolesData?.organisation?.roles), [rolesData]);

  // Filter search results to exclude users already in this organization and inactive users
  const filteredUsers = useMemo(() => {
    const users = searchData?.findUsersInAnyOrganisation?.users || [];
    return users.filter((user) => {
      const isMember = user.organisationMemberships?.some((membership) => membership.organisationId === organisationId);
      const isInactive = !user.enabled || !user.acceptedTerms;

      return !isMember && !isInactive;
    });
  }, [searchData, organisationId]);

  const selectedUsersList = useMemo(() => {
    return Array.from(basketUsers)
      .map((username) => basketUserDetails.get(username))
      .filter((u): u is AssignableUser => u !== undefined);
  }, [basketUsers, basketUserDetails]);

  const handleAddToBasket = (user: AssignableUser) => {
    const newBasket = new Set(basketUsers);
    newBasket.add(user.username);
    setBasketUsers(newBasket);

    // Store the full user object
    const newDetails = new Map(basketUserDetails);
    newDetails.set(user.username, user);
    setBasketUserDetails(newDetails);

    setSearchTerm(''); // Clear search after adding
  };

  const handleRemoveFromBasket = (username: string) => {
    const newBasket = new Set(basketUsers);
    newBasket.delete(username);
    setBasketUsers(newBasket);

    // Remove from stored details
    const newDetails = new Map(basketUserDetails);
    newDetails.delete(username);
    setBasketUserDetails(newDetails);

    // Also remove permission assignment
    const newAssignments = new Map(userAssignments);
    newAssignments.delete(username);
    setUserAssignments(newAssignments);
  };

  const handleClearBasket = () => {
    setBasketUsers(new Set());
    setBasketUserDetails(new Map());
    setUserAssignments(new Map());
  };

  const handleSetPermission = (username: string, permissionGroupId: string) => {
    const newAssignments = new Map(userAssignments);
    newAssignments.set(username, permissionGroupId);
    setUserAssignments(newAssignments);
  };

  const handleNext = () => {
    if (step === 'select' && basketUsers.size > 0) {
      setStep('permissions');
    } else if (step === 'permissions') {
      setStep('confirm');
    }
  };

  const handleBack = () => {
    if (step === 'permissions') {
      setStep('select');
    } else if (step === 'confirm') {
      setStep('permissions');
    }
  };

  const handleAssign = async () => {
    const results = {
      success: 0,
      failed: 0,
      errors: [] as Array<{username: string; error: string}>,
    };

    try {
      for (const user of selectedUsersList) {
        try {
          await createUser({
            variables: {
              input: {
                organisationId,
                username: user.username,
              },
            },
          });

          // Apply permission group if one was selected
          const permissionGroupId = userAssignments.get(user.username);
          if (permissionGroupId) {
            await setPermissionGroup({
              variables: {
                usernames: [user.username],
                organisationId,
                permissionGroupId,
              },
            });
          }

          results.success++;
        } catch (err: any) {
          console.error('Failed to add user:', err);
          results.failed++;
          results.errors.push({
            username: user.username,
            error: err.message || 'Unknown error',
          });
        }
      }

      setImportResults(results);
      setStep('result');

      if (results.success > 0) {
        setTimeout(() => {
          onSuccess();
        }, 1500);
      }
    } catch (err: any) {
      console.error('Failed to assign users:', err);
      results.failed = basketUsers.size;
      results.errors.push({
        username: 'All users',
        error: err.message || 'Unknown error',
      });
      setImportResults(results);
      setStep('result');
    }
  };

  const resetModal = () => {
    setStep('select');
    setSelectedUsers(new Set());
    setBasketUsers(new Set());
    setBasketUserDetails(new Map());
    setUserAssignments(new Map());
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setImportResults(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  const canProceedToPermissions = basketUsers.size > 0;
  const canProceedToConfirm = selectedUsersList.every((user) => userAssignments.has(user.username));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <UserPlus size={24} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Add Users to Organization</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{organisationName}</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <X size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Progress indicator */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className={`flex items-center space-x-2 ${step === 'select' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`}>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'select' ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}
              >
                1
              </div>
              <span className="font-medium">Select Users</span>
            </div>
            <div className="flex-1 h-0.5 bg-gray-200 dark:bg-gray-700 mx-4" />
            <div className={`flex items-center space-x-2 ${step === 'permissions' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`}>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'permissions' || step === 'confirm' || step === 'result' ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}
              >
                2
              </div>
              <span className="font-medium">Set Permissions</span>
            </div>
            <div className="flex-1 h-0.5 bg-gray-200 dark:bg-gray-700 mx-4" />
            <div
              className={`flex items-center space-x-2 ${step === 'confirm' || step === 'result' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'confirm' || step === 'result' ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}
              >
                3
              </div>
              <span className="font-medium">Confirm</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {/* Step 1: Select Users - Shopping Basket Design */}
          {step === 'select' && (
            <div className="grid grid-cols-2 gap-6 h-full">
              {/* Left: Search & Add */}
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Search Users</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Find users and add them to your selection basket</p>
                </div>

                {/* Search */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name, email, or username..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={searchLoading}
                    className="w-full px-4 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                  />
                  {(searchLoading || (searchTerm !== debouncedSearchTerm && searchTerm.trim() !== '')) && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>

                {/* Search Results */}
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {searchLoading ? (
                    <div className="text-center py-8">
                      <div className="w-8 h-8 mx-auto border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-2"></div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Searching...</p>
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      {debouncedSearchTerm.trim() === '' ? (
                        <div>
                          <Users size={48} className="mx-auto mb-2 opacity-20" />
                          <p>Start typing to search for users</p>
                          <p className="text-xs mt-1">Searches username, name, and email</p>
                        </div>
                      ) : (
                        <div>
                          <Users size={48} className="mx-auto mb-2 opacity-20" />
                          <p>No users found matching "{debouncedSearchTerm}"</p>
                          <p className="text-xs mt-1">Try different search terms</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    filteredUsers.map((user) => {
                      const isInBasket = basketUsers.has(user.username);
                      const isInactive = !user.enabled || !user.acceptedTerms;
                      const inactiveReason = !user.enabled ? 'Disabled' : !user.acceptedTerms ? 'Terms not accepted' : '';

                      return (
                        <div
                          key={user.id}
                          className={`flex items-center space-x-3 p-3 rounded-lg border transition-all ${
                            isInactive
                              ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 opacity-75'
                              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <div
                                className={`font-medium ${
                                  isInactive ? 'text-gray-600 dark:text-gray-400 line-through' : 'text-gray-900 dark:text-gray-100'
                                }`}
                              >
                                {user.name}
                              </div>
                              {isInactive && (
                                <span className="px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded">
                                  {inactiveReason}
                                </span>
                              )}
                            </div>
                            <div
                              className={`text-sm truncate ${isInactive ? 'text-gray-500 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'}`}
                            >
                              {user.email}
                            </div>
                          </div>
                          {isInBasket ? (
                            <div className="flex items-center space-x-2">
                              <CheckCircle size={20} className="text-green-600 dark:text-green-400" />
                              <span className="text-sm text-green-600 dark:text-green-400 font-medium">Added</span>
                            </div>
                          ) : isInactive ? (
                            <div className="px-3 py-1 text-xs text-red-600 dark:text-red-400 font-medium">Inactive</div>
                          ) : (
                            <button
                              onClick={() => handleAddToBasket(user)}
                              className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1"
                            >
                              <UserPlus size={16} />
                              <span>Add</span>
                            </button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Right: Shopping Basket */}
              <div className="space-y-4 border-l border-gray-200 dark:border-gray-700 pl-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">Selected Users</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {basketUsers.size} user{basketUsers.size !== 1 ? 's' : ''} in basket
                    </p>
                  </div>
                  {basketUsers.size > 0 && (
                    <button onClick={handleClearBasket} className="text-sm text-red-600 dark:text-red-400 hover:underline">
                      Clear All
                    </button>
                  )}
                </div>

                {/* Basket Contents */}
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {basketUsers.size === 0 ? (
                    <div className="text-center py-12 text-gray-400 dark:text-gray-500">
                      <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                        <Users size={32} className="opacity-50" />
                      </div>
                      <p>Your basket is empty</p>
                      <p className="text-sm mt-1">Search and add users to get started</p>
                    </div>
                  ) : (
                    selectedUsersList.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center space-x-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 dark:text-gray-100">{user.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</div>
                        </div>
                        <button
                          onClick={() => handleRemoveFromBasket(user.username)}
                          className="p-1 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                          title="Remove from basket"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Set Permissions */}
          {step === 'permissions' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">Set Permission Group</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">Select a group for each user</p>
                </div>
                <div className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                  {selectedUsersList.filter((u) => userAssignments.has(u.username)).length} / {selectedUsersList.length} configured
                </div>
              </div>

              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                {selectedUsersList.map((user) => {
                  const isConfigured = userAssignments.has(user.username);
                  const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase();
                  return (
                    <div
                      key={user.id}
                      className={`rounded-lg border p-4 ${
                        isConfigured
                          ? 'border-gray-200 dark:border-gray-700'
                          : 'border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/10'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                            isConfigured
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                              : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                          }`}
                        >
                          {initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900 dark:text-gray-100">{user.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</div>
                        </div>
                        {!isConfigured && (
                          <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Needs permission</span>
                        )}
                      </div>
                      <RadioCardSelect
                        options={roles}
                        value={userAssignments.get(user.username) || null}
                        onChange={(groupId) => handleSetPermission(user.username, groupId)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 'confirm' && (
            <div className="space-y-4">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle size={20} className="text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-1">Please Review Before Proceeding</h4>
                    <p className="text-sm text-yellow-800 dark:text-yellow-300">
                      You are about to add {basketUsers.size} user{basketUsers.size !== 1 ? 's' : ''} to <strong>{organisationName}</strong>. Please
                      review the details below and confirm this action.
                    </p>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Assignment Summary</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {selectedUsersList.map((user) => {
                    const permissionGroupId = userAssignments.get(user.username);
                    const role = roles.find((r) => r.id === permissionGroupId);

                    return (
                      <div key={user.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 dark:text-gray-100">{user.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</div>
                        </div>
                        <div className="ml-4 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-sm font-medium rounded-full">
                          {role?.name || 'No role'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Result */}
          {step === 'result' && importResults && (
            <div className="space-y-4">
              {importResults.success > 0 && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle size={24} className="text-green-600 dark:text-green-400 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-green-900 dark:text-green-100 text-lg mb-1">Users Added Successfully!</h3>
                      <p className="text-sm text-green-800 dark:text-green-300">
                        Successfully added {importResults.success} user{importResults.success !== 1 ? 's' : ''} to {organisationName}.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {importResults.failed > 0 && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle size={24} className="text-red-600 dark:text-red-400 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-red-900 dark:text-red-100 text-lg mb-2">Some Users Failed</h3>
                      <p className="text-sm text-red-800 dark:text-red-300 mb-3">
                        {importResults.failed} user{importResults.failed !== 1 ? 's' : ''} could not be added:
                      </p>
                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {importResults.errors.map((err, idx) => (
                          <div key={idx} className="text-sm text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 p-2 rounded">
                            <strong>{err.username}:</strong> {err.error}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            {step === 'result' ? 'Close' : 'Cancel'}
          </button>

          <div className="flex space-x-3">
            {step !== 'select' && step !== 'result' && (
              <button
                onClick={handleBack}
                disabled={assigningUsers}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Back
              </button>
            )}

            {step === 'select' && (
              <button
                onClick={handleNext}
                disabled={!canProceedToPermissions}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <span>Next: Set Permissions</span>
                <span className="text-sm">({basketUsers.size})</span>
              </button>
            )}

            {step === 'permissions' && (
              <button
                onClick={handleNext}
                disabled={!canProceedToConfirm}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next: Review & Confirm
              </button>
            )}

            {step === 'confirm' && (
              <button
                onClick={handleAssign}
                disabled={assigningUsers}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {assigningUsers ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Adding Users...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle size={20} />
                    <span>Confirm & Add Users</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
